import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scale, FileText, Building, Search, ShoppingCart, ChevronLeft, ChevronRight } from "lucide-react";
import { servicesData } from "../data/mock";

const iconMap = {
  Scale: Scale,
  FileText: FileText,
  Building: Building,
  Search: Search,
  ShoppingCart: ShoppingCart
};

const ServicesSection = ({ onOpenForm }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % (servicesData.length + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + (servicesData.length + 1)) % (servicesData.length + 1));
  };

  return (
    <section id="services" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-custom px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">
            Our Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">
            Comprehensive Trademark & IP Services
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From trademark registration to copyright protection, we offer a complete suite of intellectual 
            property services designed to safeguard your business identity and creative works. Our expert 
            team handles every aspect of IP protection with precision and care.
          </p>
        </div>

        {/* Services Grid - Desktop */}
        {!isMobile && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden card-hover"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <IconComponent className="w-7 h-7 text-[#1e3a8a]" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#1e3a8a] transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.shortDescription}
                    </p>

                    {/* Features */}
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                          <span className="text-[#d4af37] mt-1">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center text-[#1e3a8a] font-semibold group-hover:text-[#d4af37] transition-colors duration-300 pt-4">
                      <span>Learn More</span>
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* CTA Box */}
            <div className="group bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden card-hover p-8 flex flex-col justify-center items-center text-center text-white">
              <h3 className="text-2xl font-bold mb-4 text-white">
                Ready to Protect Your Brand?
              </h3>
              <p className="text-white mb-6 leading-relaxed">
                Get started with our comprehensive trademark registration services today and secure your business identity.
              </p>
              <button
                onClick={onOpenForm}
                className="bg-white text-[#1e3a8a] px-8 py-4 rounded-lg font-bold hover:bg-[#d4af37] hover:text-white transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg"
              >
                <span>Register My Trademark</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Services Carousel - Mobile */}
        {isMobile && (
          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {servicesData.map((service, index) => {
                  const IconComponent = iconMap[service.icon];
                  return (
                    <div key={service.id} className="w-full flex-shrink-0 px-4">
                      <Link
                        to={`/services/${service.id}`}
                        className="block bg-white rounded-2xl shadow-lg overflow-hidden"
                      >
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/80 to-transparent"></div>
                          <div className="absolute bottom-4 left-4">
                            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                              <IconComponent className="w-7 h-7 text-[#1e3a8a]" />
                            </div>
                          </div>
                        </div>

                        <div className="p-6 space-y-4">
                          <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                          <p className="text-gray-600 leading-relaxed">{service.shortDescription}</p>

                          <ul className="space-y-2">
                            {service.features.slice(0, 3).map((feature, idx) => (
                              <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                                <span className="text-[#d4af37] mt-1">✓</span>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="flex items-center text-[#1e3a8a] font-semibold pt-4">
                            <span>Learn More</span>
                            <ArrowRight className="w-5 h-5 ml-2" />
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}

                {/* CTA Slide */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-2xl shadow-lg p-8 flex flex-col justify-center items-center text-center text-white min-h-[500px]">
                    <h3 className="text-2xl font-bold mb-4 text-white">
                      Ready to Protect Your Brand?
                    </h3>
                    <p className="text-white mb-6 leading-relaxed">
                      Get started with our comprehensive trademark registration services today and secure your business identity.
                    </p>
                    <button
                      onClick={onOpenForm}
                      className="bg-white text-[#1e3a8a] px-8 py-4 rounded-lg font-bold hover:bg-[#d4af37] hover:text-white transition-all duration-300 flex items-center space-x-2 shadow-lg"
                    >
                      <span>Register My Trademark</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-8 space-x-2">
              {[...Array(servicesData.length + 1)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index ? "bg-[#1e3a8a] w-8" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
